import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { Image, Platform, ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import AsyncStorage from '@react-native-async-storage/async-storage';

import Colors from '../../constants/Colors';
import Languages from '../../constants/Languages';
import Layout from '../../constants/Layout';
import * as Actions from '../../store/Actions';
import Header from '../../components/register/Header';
import FilledButton from '../../components/FilledButton';
import Svg from '../../components/Svg';
import PtfKeyboardAvoidingView from '../../components/PtfKeyboardAvoidingView';
import MyFloatingLabelInput from '../../components/form/MyFloatingLabelInput';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.mediumBlue,
  },
  mainContentContainer: {
    alignItems: 'center',
    backgroundColor: Colors.white,
  },
  topContainer: {
    alignItems: 'center',
  },
  emailContainer: {
    marginTop: 132,
  },
  headerText: {
    fontFamily: 'roboto-regular',
    fontSize: Layout.isSmall ? 20 : 26,
    color: Colors.veryDarkGray,
    textAlign: 'center',
    marginTop: Layout.isSmall ? 34 : 50,
    width: 328,
  },
  text: {
    fontFamily: 'roboto-regular',
    fontSize: Layout.isSmall ? 14 : 16,
    color: Colors.veryDarkGray,
    textAlign: 'center',
    marginTop: Layout.isSmall ? 16 : 8,
    width: Layout.isSmall ? 269 : 330,
  },
  image: {
    height: 101,
    width: 69,
    resizeMode: 'contain',
    marginTop: Layout.isSmall ? 33 : 79,
    marginLeft: -10,
  },
  form: {
    marginLeft: -15, // to align inputs within form
    marginBottom: 16,
    padding: 0,
    width: 345,
  },
  input: {
    fontSize: 16,
    lineHeight: 24,
    fontFamily: 'roboto-regular',
  },
  signupContainer: Layout.signupContainer,
  signupFooter: Layout.signupFooter,
  linkContainer: {
    paddingHorizontal: 16,
    paddingTop: Layout.isSmall ? 20 : 32,
    paddingBottom: 6,
    justifyContent: 'center',
    alignItems: 'center',
  },
  doNotAskContainer: {
    flexDirection: 'row',
  },
  checkbox: {
    width: 32,
  },
  label: {
    fontFamily: 'roboto-regular',
    fontSize: Layout.isSmall ? 16 : 14,
    color: Colors.veryDarkGray,
  },
  linkTextContainer: {
    marginBottom: 24,
  },
  linkText: {
    fontSize: Layout.isSmall ? 14 : 16,
    color: Colors.darkGreen,
    textDecorationLine: 'underline',
    textTransform: 'uppercase',
    fontWeight: '500',
  },
  errorLabel: {
    fontFamily: 'roboto-regular',
    fontSize: Layout.isSmall ? 16 : 14,
    color: Colors.brightRed,
    marginBottom: 8,
  },
});

class EnterEmailScreen extends Component {
  static propTypes = {
    registration: PropTypes.objectOf(PropTypes.any).isRequired,
    actions: PropTypes.objectOf(PropTypes.any).isRequired,
    navigation: PropTypes.objectOf(PropTypes.any).isRequired,
  };

  constructor(props) {
    super();
    this.state = {
      email: '',
      isValid: false,
      doNotAskAgain: false,
      emailError: null,
    };
    this.scroll = null;
  }

  componentDidMount() {}

  componentDidUpdate = () => {
    const { registration } = this.props;
    const { emailError } = this.state;
    if (registration.enterEmail.error && !emailError) {
      this.setState({ emailError: registration.enterEmail.error });
    }
  };

  validate = (value) => {
    if (!value) return false;
    if (value.indexOf('@') !== -1 && value.indexOf('.') !== -1 && value.indexOf('.') < value.length - 2)
      return true;
  };

  goBack = () => {
    const { navigation } = this.props;
    navigation.navigate('PhoneOrEmail');
  };

  goNext = () => {
    const { actions, navigation } = this.props;
    const { email, isValid } = this.state;
    if (isValid)
      actions.enterEmail(email, () => {
        navigation.navigate('VerificationEmailSent');
      });
  };

  skip = () => {
    const { navigation, registration, actions } = this.props;
    const { doNotAskAgain } = this.state;
    const { flow, user } = registration;
    if (doNotAskAgain) {
      actions.doNotAskAgain('email');
    }
    if (flow === 'new_phone') {
      navigation.navigate('TermsPrivacy');
    } else if (user.docs && user.docs.length) {
      navigation.navigate('TermsPrivacy');
    } else if (!user.onboarding_complete) {
      navigation.navigate('UserDetails');
    } else {
      AsyncStorage.setItem('onboarded', '1').then(() => {
        actions.refreshRedux();
        navigation.navigate('a');
      });
    }
  };

  onUpdateText = (text) => {
    const { actions, registration } = this.props;
    const { enterEmail } = registration;
    if (enterEmail.error) actions.clearEnterEmailError();
    const value = text.trim();
    const isValid = this.validate(value);
    this.setState({ email: value, isValid, emailError: null });
  };

  onPressDoNotAsk = () => {
    const { doNotAskAgain } = this.state;
    this.setState({ doNotAskAgain: !doNotAskAgain });
  };

  render() {
    const { registration } = this.props;
    const { email, isValid, doNotAskAgain } = this.state;
    const { language, enterEmail } = registration;
    const translation = Languages.translations[language?.name || 'en'];
    return (
      <SafeAreaView style={{ flex: 1 }} edges={['top', 'left', 'right', 'bottom']}>
        <PtfKeyboardAvoidingView style={{ flex: 1 }}>
          <ScrollView
            contentContainerStyle={styles.mainContentContainer}
            style={styles.container}
            testID="enter-email"
          >
            <Header title={translation.sign_in} goBack={this.goBack} />
            <View style={styles.signupContainer}>
              <View style={styles.topContainer}>
                <Image source={require('../../assets/images/sign_up/image_36.png')} style={styles.image} />
                <Text style={styles.headerText}>{translation.enter_email}</Text>
                <Text style={styles.text}>{translation.email_address_purpose}</Text>
                <View style={styles.emailContainer}>
                  <MyFloatingLabelInput
                    label={translation.email_address}
                    value={email}
                    onChangeText={this.onUpdateText}
                    onSubmitEditing={this.goNext}
                    width={328}
                    error={enterEmail.error ? translation[enterEmail.error] : ''}
                  />
                </View>
              </View>
              <View style={styles.signupFooter}>
                <FilledButton
                  onPress={this.goNext}
                  label={translation.fragment_registration_language_button_next}
                  color={Colors.primaryColor}
                  full
                  textColor={Colors.white}
                  disabled={!isValid}
                  testID="button-next"
                />
                <View style={styles.linkContainer}>
                  <TouchableOpacity onPress={this.skip} style={styles.linkTextContainer} testID="skip">
                    <Text style={styles.linkText}>{translation.skip}</Text>
                  </TouchableOpacity>
                  <View style={styles.doNotAskContainer}>
                    <TouchableOpacity
                      style={styles.checkbox}
                      onPress={this.onPressDoNotAsk}
                      testID="do-not-ask"
                    >
                      <Svg
                        icon={doNotAskAgain ? 'square_checked' : 'square_unchecked'}
                        style={styles.checkbox}
                      />
                    </TouchableOpacity>
                    <Text style={styles.label}>{translation.do_not_ask_again}</Text>
                  </View>
                </View>
              </View>
            </View>
          </ScrollView>
        </PtfKeyboardAvoidingView>
      </SafeAreaView>
    );
  }
}

function mapStateToProps(state) {
  return {
    registration: state.registration,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(Actions, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(EnterEmailScreen);
